import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { ListParams } from '@/types/api'
import type { StoredFile, StudentDetail, StudentFilters, StudentStatus, StudentSummary } from './types'

export const studentKeys = {
  all: ['students'] as const,
  list: (params: object) => [...studentKeys.all, 'list', params] as const,
  detail: (id: number) => [...studentKeys.all, 'detail', id] as const,
}

export const studentsQuery = (params: ListParams & StudentFilters) =>
  queryOptions({
    queryKey: studentKeys.list(params),
    queryFn: () => api.page<StudentSummary>('/students', params),
    placeholderData: keepPreviousData,
  })

export const studentQuery = (id: number) =>
  queryOptions({ queryKey: studentKeys.detail(id), queryFn: () => api.get<StudentDetail>(`/students/${id}`) })

export interface StudentPayload {
  admissionNumber: string
  firstName: string
  lastName: string
  gender: string
  dateOfBirth: string | null
  bloodGroup: string | null
  religion: string | null
  nationality: string | null
  email: string | null
  phone: string | null
  address: string | null
  medicalNotes: string | null
  photoFileId: number | null
  guardians: {
    id: number | null
    relation: string
    name: string
    phone: string | null
    email: string | null
    occupation: string | null
    isPrimary: boolean
  }[]
}

export interface AdmitPayload extends StudentPayload {
  sectionId: number
  rollNumber: string | null
  admissionDate: string
}

function useStudentMutation<TVars>(fn: (vars: TVars) => Promise<StudentDetail>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (student) => {
      qc.setQueryData(studentKeys.detail(student.summary.id), student)
      void qc.invalidateQueries({ queryKey: studentKeys.all })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
      void qc.invalidateQueries({ queryKey: ['academic', 'classes'] })
    },
  })
}

export const useAdmitStudent = () =>
  useStudentMutation((payload: AdmitPayload) => api.post<StudentDetail>('/students', payload))
export const useUpdateStudent = () =>
  useStudentMutation(({ id, ...payload }: StudentPayload & { id: number }) =>
    api.put<StudentDetail>(`/students/${id}`, payload),
  )
export const usePromoteStudent = () =>
  useStudentMutation(
    ({ id, ...body }: { id: number; academicYearId: number; sectionId: number; rollNumber: string | null }) =>
      api.post<StudentDetail>(`/students/${id}/promote`, body),
  )
export const useChangeSection = () =>
  useStudentMutation(({ id, ...body }: { id: number; sectionId: number; rollNumber: string | null }) =>
    api.patch<StudentDetail>(`/students/${id}/section`, body),
  )
export const useChangeStudentStatus = () =>
  useStudentMutation(({ id, status }: { id: number; status: StudentStatus }) =>
    api.patch<StudentDetail>(`/students/${id}/status`, { status }),
  )
export const useCreatePortalAccount = () =>
  useStudentMutation(
    ({ id, ...body }: { id: number; guardianId: number | null; email: string; password: string }) =>
      api.post<StudentDetail>(`/students/${id}/portal-account`, body),
  )

export function useStudentDocuments(studentId: number) {
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: studentKeys.detail(studentId) })
  return {
    attach: useMutation({
      mutationFn: (fileId: number) => api.post<StoredFile>(`/students/${studentId}/documents`, { fileId }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (fileId: number) => api.delete(`/students/${studentId}/documents/${fileId}`),
      onSuccess: refresh,
    }),
  }
}
